import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  Send,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  Plus,
  CreditCard,
  AlertTriangle,
  Crown,
  ShieldCheck,
  Users,
  MessageSquare,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/inboxes", label: "Inboxes", icon: Inbox },
  { to: "/campaigns", label: "Campaigns", icon: Send },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/unibox", label: "Unibox", icon: MessageSquare },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/audit", label: "Auditor", icon: ShieldCheck },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function AppLayout() {
  const { user, billing, logout, isAdmin, enterpriseMode, toggleEnterpriseMode } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inGrace =
    billing?.access_reason === "grace" && billing?.grace_period_end;
  const isLifetime = billing?.access_reason === "lifetime";

  return (
    <div className="app-shell min-h-screen bg-[#0a0a0a] text-white flex">
      <aside
        className="hidden md:flex w-64 flex-col border-r border-[#1a1a1a] bg-[#0a0a0a] sticky top-0 h-screen"
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-[#1a1a1a]">
          <Logo variant="white" className="h-7" />
        </div>
        {isAdmin && enterpriseMode && <WorkspaceSwitcher />}
        <button
          onClick={() => navigate("/campaigns/new")}
          data-testid="new-campaign-button"
          className="mx-4 mt-4 flex items-center justify-center gap-2 bg-white text-black rounded-md py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={1.8} />
          New campaign
        </button>
        <nav className="flex-1 px-2 py-4 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#161616] text-white"
                    : "text-neutral-400 hover:text-white hover:bg-[#121212]"
                }`
              }
              end={to === "/billing"}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1a1a1a]">
          {isAdmin && (
            <button
              onClick={toggleEnterpriseMode}
              data-testid="enterprise-mode-toggle"
              aria-pressed={enterpriseMode}
              title="Toggle Enterprise (agency) mode"
              className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md border border-[#1f1f1f] bg-[#0d0d0d] hover:bg-[#141414] transition-colors"
            >
              <span className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-neutral-400">
                <Crown className="w-3.5 h-3.5 text-amber-400" strokeWidth={1.8} />
                Enterprise mode
              </span>
              <span
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  enterpriseMode ? "bg-amber-400/80" : "bg-[#2a2a2a]"
                }`}
                data-testid="enterprise-mode-state"
                data-state={enterpriseMode ? "on" : "off"}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                    enterpriseMode ? "translate-x-4" : ""
                  }`}
                />
              </span>
            </button>
          )}
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#1a1a1a]" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate flex items-center gap-1.5">
                {user?.name}
                {isLifetime && (
                  <Crown
                    className="w-3 h-3 text-amber-400"
                    strokeWidth={1.8}
                    data-testid="lifetime-crown"
                  />
                )}
              </div>
              <div className="text-xs text-neutral-500 truncate font-mono">
                {user?.email}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {inGrace && (
          <div
            className="border-b border-amber-400/20 bg-amber-400/5 text-amber-200 text-sm px-8 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="grace-banner"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" strokeWidth={1.8} />
              Payment failed - you have until{" "}
              <span className="font-mono">
                {new Date(billing.grace_period_end).toLocaleString()}
              </span>{" "}
              to update your card.
            </div>
            <button
              onClick={() => navigate("/billing")}
              className="text-xs font-mono uppercase tracking-[0.2em] bg-amber-400/20 text-amber-200 hover:bg-amber-400/30 rounded-md px-3 py-1.5"
            >
              Fix payment
            </button>
          </div>
        )}
        <header className="sticky top-0 z-20 border-b border-[#1a1a1a] backdrop-blur-xl bg-[rgba(10,10,10,0.8)]">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-neutral-500">
              {location.pathname.replace("/", "") || "Dashboard"}
            </div>
            <div className="text-xs text-neutral-500 font-mono">
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </div>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
